import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useNavigation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlProjectId = searchParams.get('projectId');
  const [isSaving, setIsSaving] = useState(false);

  const handleBack = async () => {
    if (!urlProjectId) {
      toast.error("Project ID is missing");
      return;
    }
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('project_requirements')
        .select('id')
        .eq('id', urlProjectId)
        .maybeSingle();

      if (projectError) {
        console.error('Error fetching project data:', projectError);
        toast.error("Failed to fetch project data");
        return;
      }

      if (!projectData?.id) {
        toast.error("No project ID found");
        return;
      }

      navigate(`/dashboard?step=1&projectId=${projectData.id}`);
      toast.success("Returning to Requirements");
    } catch (error) {
      console.error('Error in handleBack:', error);
      toast.error("Failed to navigate back");
    }
  };

  const handleNext = async (nodes: any[], edges: any[], handleSaveToSupabase: Function) => {
    if (!urlProjectId) {
      toast.error("Project ID is missing");
      return;
    }
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('project_requirements')
        .select('project_id')
        .eq('id', urlProjectId)
        .maybeSingle();

      if (projectError) {
        console.error('Error fetching project data:', projectError);
        toast.error("Failed to fetch project data");
        return;
      }

      if (!projectData?.project_id) {
        toast.error("No project ID found");
        return;
      }

      const { data: structureData, error: structureError } = await supabase
        .from('organization_structures')
        .select('is_saved')
        .eq('project_id', projectData.project_id)
        .maybeSingle();

      if (structureError) {
        console.error('Error checking organization structure:', structureError);
        toast.error("Failed to check organization structure status");
        return;
      }

      if (!structureData?.is_saved) {
        toast.error("Please save the organization structure first");
        return;
      }

      const updatedNodes = nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          is_editable: false
        }
      }));
      
      await handleSaveToSupabase(updatedNodes, edges, false);

      await supabase
        .from('organization_structures')
        .update({ is_editable: false })
        .eq('project_id', projectData.project_id);

      // Fetch nodes data
      const {
        data: nodesData,
        error: nodesError
      } = await supabase.from('nodes').select('node_id, label, project_id, name').eq('project_id', projectData.project_id);
      if (nodesError) {
        console.error('Error fetching nodes:', nodesError);
        toast.error("Failed to fetch nodes");
        return;
      }
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        toast.error("No user session found");
        return;
      }

      // Fetch existing role_jd entries with has_jd = true
      const {
        data: existingRoles,
        error: existingRolesError
      } = await supabase.from('role_jd').select('role_title, role_description, has_jd').eq('project_id', projectData.project_id).eq('has_jd', true);
      if (existingRolesError) {
        console.error('Error fetching existing roles:', existingRolesError);
        toast.error("Failed to fetch existing roles");
        return;
      }

      // Create a map of existing roles with has_jd = true
      const existingRoleMap = new Map(existingRoles?.map(role => [role.role_title, role.role_description]) || []);

      // Delete existing role_jd entries that don't have has_jd = true
      const {
        error: deleteError
      } = await supabase.from('role_jd').delete().eq('project_id', projectData.project_id).eq('has_jd', false);
      if (deleteError) {
        console.error('Error deleting existing roles:', deleteError);
        toast.error("Failed to update roles");
        return;
      }

      // Insert or update role_jd entries
      for (const node of nodesData) {
        const existingRoleDescription = existingRoleMap.get(node.label);
        const roleData = {
          project_id: node.project_id,
          name: node.name,
          role_title: node.label,
          role_description: existingRoleDescription || {
            text: "No JD found"
          },
          user_id: session.user.id,
          has_jd: !!existingRoleDescription
        };
        const {
          error: upsertError
        } = await supabase.from('role_jd').upsert(roleData, {
          onConflict: 'project_id,role_title',
          ignoreDuplicates: false
        });
        if (upsertError) {
          console.error('Error upserting role:', upsertError);
          toast.error("Failed to create/update role entry");
          return;
        }
      }

      navigate(`/dashboard?step=3&projectId=${projectData.project_id}`);
      toast.success("Moving to Define Roles");
    } catch (error) {
      console.error('Error in handleNext:', error);
      toast.error("Failed to proceed to next step");
    }
  };

  return {
    handleBack,
    handleNext,
    isSaving,
    setIsSaving,
    urlProjectId
  };
};
